import { type ServicesState, useServicesStore } from 'hooks/use-services'

export function getServiceHasBeenDeletedMessage(serviceId: string) {
  return `Service no longer exists (${serviceId})`
}

export function getIsServiceDeleted(
  serviceId: string,
  servicesState: ServicesState
) {
  const isFetchingPagesComplete =
    servicesState.status === 'success' && !servicesState.hasNextPage

  return !servicesState.services[serviceId] && isFetchingPagesComplete
}

/**
 * Given a service id, returns the service (if found) and whether or not it has been deleted.
 * See `useMarkServicesAsDeleted` for more details.
 */
export function useGetServiceAndMarkIsDeleted(serviceId: string | undefined) {
  const servicesState = useServicesStore()

  const isServiceDeleted = serviceId
    ? getIsServiceDeleted(serviceId, servicesState)
    : false

  return {
    isServiceDeleted,
    service: serviceId ? servicesState.services[serviceId] : undefined
  }
}

/**
 * Given a list of service references (a service or service run),
 * marks each service with an `isServiceDeleted` flag if it isn't found in the UI services store.
 *
 * A service has most likely been deleted if it's not found in the store.
 * We have to check that all pages have been fetched to make that determination.
 *
 * @param items test runs, services, etc
 * @param idGetter picks the service id path. example: (x) => x.serviceId
 */
export function useMarkServicesAsDeleted<T>(
  items: T[],
  idGetter: (item: T) => string
) {
  const servicesState = useServicesStore()

  return items.map((item) => {
    return {
      ...item,
      isServiceDeleted: getIsServiceDeleted(idGetter(item), servicesState)
    }
  })
}
